import React, { useMemo } from "react";
import { Box, CircularProgress, IconButton, Tooltip } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeAlpine } from "ag-grid-community";
import { AiOutlineFileSearch } from "react-icons/ai";
import { MdOutlineContentCopy } from "react-icons/md";
import { Link } from "react-router-dom";
import AgGridPagination from "../../../shared/AgGridPagination";

ModuleRegistry.registerModules([AllCommunityModule]);

type RowData = {
  feegroup_id: string;
  name: string;
  description: string;
};

type Props = {
  isLoading: boolean;
  tableData: RowData[];
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: any;
};

const FeeGroupAgentList = (props: Props) => {
  const { isLoading, tableData, page, pageSize, rowCount, setPaginationModel } = props;
  const userRole = localStorage.getItem("userRole")?.toLowerCase() || "";

  const handleClickCopy = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch (err) {
      console.error("Failed to copy ID: ", err);
    }
  };

  const CustomLoadingOverlay = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <CircularProgress size={40} />
    </div>
  );

  const CopyIdRenderer = (params: ICellRendererParams) => (
    <Tooltip title="Copy ID" arrow placement="top">
      <div>
        <IconButton
          id={"button-copy-id-" + params.data?.feegroup_id}
          onClick={() => handleClickCopy(params.data.feegroup_id)}
          size="small"
        >
          <MdOutlineContentCopy className="h-5 w-5 text-navy-700" />
        </IconButton>
      </div>
    </Tooltip>
  );

  const ActionsRenderer = (params: ICellRendererParams) => (
    <Tooltip title="View More Details" arrow placement="right">
      <div>
        <IconButton
          component={Link}
          to={`/${userRole}/fee-groups/agent/${params.data.feegroup_id}`}
          size="small"
        >
          <AiOutlineFileSearch className="h-5 w-5 text-navy-700" />
        </IconButton>
      </div>
    </Tooltip>
  );

  const columns: ColDef[] = [
    {
      field: "id",
      headerName: "",
      width: 50,
      sortable: false,
      resizable: false,
      cellRenderer: CopyIdRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "name",
      headerName: "Name",
      sortable: false,
      minWidth: 100,
      flex: 1,
    },
    {
      field: "description",
      headerName: "Description",
      sortable: false,
      minWidth: 140,
      flex: 2,
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      width: 80,
      resizable: false,
      cellRenderer: ActionsRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
    },
  ];

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      suppressMenu: true,
    }),
    []
  );

  const hidePagination = rowCount <= pageSize;

  return (
    <Box className="card w-full">
      <div style={{ width: "100%" }}>
        <AgGridReact
          theme={themeAlpine}
          rowData={isLoading ? [] : tableData}
          columnDefs={columns}
          defaultColDef={defaultColDef}
          getRowId={(params) => params.data.feegroup_id}
          rowHeight={48}
          headerHeight={44}
          domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
          suppressPaginationPanel={true}
          loading={isLoading}
          loadingOverlayComponent={CustomLoadingOverlay}
          overlayNoRowsTemplate="No fee group available"
        />
      </div>

      <AgGridPagination
        page={page}
        pageSize={pageSize}
        rowCount={rowCount}
        onPaginationChange={setPaginationModel}
        hidden={hidePagination}
      />
    </Box>
  );
};

export default FeeGroupAgentList;
